import { router, useForm } from "@inertiajs/react";
import { Check, Edit3, Lock, X } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type EditableValue = string | number | boolean | null | undefined;

interface InlineEditableFieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
  field: string;
  type?: "text" | "textarea" | "select" | "date" | "datetime" | "number" | "boolean";
  updateUrl?: string;
  icon?: React.ElementType;
  options?: { label: string; value: string | number | boolean }[];
  placeholder?: string;
  disabled?: boolean;
  formatValue?: (value: string | number | boolean | null | undefined) => string;
  maxLength?: number;
}

const defaultFormatValue = (
  value: string | number | boolean | null | undefined,
) => {
  if (value === null || value === undefined) return "No especificado";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  return String(value);
};

export default function InlineEditableField({
  label,
  value,
  field,
  type = "text",
  updateUrl,
  icon: Icon,
  options = [],
  placeholder,
  disabled = false,
  formatValue = defaultFormatValue,
  maxLength,
}: InlineEditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [numberInput, setNumberInput] = useState<string>("");

  const { data, setData, patch, processing, errors, reset } = useForm<
    Record<string, EditableValue>
  >({
    [field]: value,
  });

  // Convert DD/MM/YYYY to YYYY-MM-DD for HTML date input
  const convertToISODate = (
    dateStr: string | number | boolean | null | undefined,
  ): string => {
    if (!dateStr || typeof dateStr !== "string") return "";

    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return dateStr;
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY for backend
  const convertFromISODate = (dateStr: string): string => {
    if (!dateStr) return "";

    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  // Convert "DD/MM/YYYY HH:mm" -> "YYYY-MM-DDTHH:mm" for HTML datetime-local
  const convertToISODatetimeLocal = (
    value: string | number | boolean | null | undefined,
  ): string => {
    if (!value || typeof value !== "string") return "";
    // Expected input: "DD/MM/YYYY HH:mm"
    const [datePart, timePart] = value.split(" ");
    if (!datePart) return "";
    const parts = datePart.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const hhmm = (timePart || "00:00").slice(0, 5);
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${hhmm}`;
    }
    // If already ISO-ish, return as is
    return value;
  };

  // Keep as ISO local for server: "YYYY-MM-DDTHH:mm"
  const normalizeDatetimeForServer = (val: string): string => {
    if (!val) return "";
    // Ensure we only keep up to minutes
    const t = val.includes("T") ? val : val.replace(" ", "T");
    return t.slice(0, 16);
  };

  const startEditing = () => {
    if (!disabled) {
      reset();
      // For date/datetime fields, convert to appropriate ISO format for HTML inputs
      const editValue =
        type === "date"
          ? convertToISODate(value)
          : type === "datetime"
            ? convertToISODatetimeLocal(value)
            : value;
      setData(field, editValue);
      setIsEditing(true);
      if (type === "number") {
        const str = value === null || value === undefined ? "" : String(value);
        setNumberInput(str.replace(".", ","));
      }
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    reset();
    setData(field, value);
  };

  const numberPattern = useMemo(() => /^[0-9]+(,[0-9]+)?$/, []);
  const isNumberValid = useMemo(() => {
    if (type !== "number") return true;
    if (numberInput.trim() === "") return false;
    return numberPattern.test(numberInput);
  }, [type, numberInput, numberPattern]);

  const saveChanges = () => {
    if (!updateUrl) {
      setIsEditing(false);
      return;
    }

    // For date fields, convert back to DD/MM/YYYY before sending to backend
    if (type === "date" && data[field] && typeof data[field] === "string") {
      const convertedValue = convertFromISODate(data[field]);
      setData(field, convertedValue);

      // Use setTimeout to ensure setData is processed before patch
      setTimeout(() => {
        patch(updateUrl, {
          preserveScroll: true,
          onSuccess: () => {
            setIsEditing(false);
          },
          onError: () => {
            // Keep editing mode open if there are errors
          },
        });
      }, 0);
    } else if (type === "datetime" && data[field] && typeof data[field] === "string") {
      const serverValue = normalizeDatetimeForServer(data[field]);
      router.patch(updateUrl, { [field]: serverValue }, {
        preserveScroll: true,
        onSuccess: () => setIsEditing(false),
        onError: () => {/* keep editing */},
      });
    } else if (type === "number") {
      if (!isNumberValid) return;
      const parsed = Number(numberInput.replace(",", "."));
      const payload = { [field]: Number.isFinite(parsed) ? parsed : null };
      router.patch(updateUrl, payload, {
        preserveScroll: true,
        onSuccess: () => setIsEditing(false),
        onError: () => {/* Intentional no-op */},
      });
    } else {
      patch(updateUrl, {
        preserveScroll: true,
        onSuccess: () => {
          setIsEditing(false);
        },
        onError: () => {
          // Keep editing mode open if there are errors
        },
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && type !== "select") {
      e.preventDefault();
      saveChanges();
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  const renderInput = () => {
    switch (type) {
      case "select":
        return (
          <Select
            value={data[field] ? String(data[field]) : undefined}
            onValueChange={(newValue) => setData(field, newValue)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem
                  key={String(option.value)}
                  value={String(option.value)}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "number":
        return (
          <Input
            type="text"
            inputMode="decimal"
            value={numberInput}
            onChange={(e) => {
              const raw = e.target.value;
              if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                setNumberInput(raw);
              }
            }}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            className="h-9"
          />
        );
      case "date":
        return (
          <Input
            type="date"
            value={String(data[field] ?? "")}
            onChange={(e) => setData(field, e.target.value)}
            onKeyDown={handleKeyPress}
            className="h-9"
            lang="es"
          />
        );
      case "boolean":
        return (
          <Select
            value={String(data[field] ?? "")}
            onValueChange={(newValue) => setData(field, newValue === "true")}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Sí</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        );
      case "datetime":
        return (
          <Input
            type="datetime-local"
            value={String(data[field] ?? "")}
            onChange={(e) => setData(field, e.target.value)}
            onKeyDown={handleKeyPress}
            className="h-9"
            lang="es-AR"
          />
        );
      case "textarea":
        return (
          <TextareaWithCounter
            value={String(data[field] ?? "")}
            onChange={(e) => setData(field, e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            className="h-[150px] max-h-[30vh] resize-none overflow-y-auto whitespace-pre-wrap"
            maxLength={maxLength}
            showCharacterCount={!!maxLength}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={String(data[field] ?? "")}
            onChange={(e) => setData(field, e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            className="h-9"
          />
        );
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          {Icon && <Icon className="h-4 w-4" />}
          {label}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">{renderInput()}</div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="default"
              onClick={saveChanges}
              disabled={processing || !isNumberValid}
              className="h-9 w-9 p-0"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancelEditing}
              disabled={processing}
              className="h-9 w-9 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {errors[field] && (
          <p className="text-xs text-red-500">{errors[field]}</p>
        )}
        {type === "number" && (
          <p className="text-xs text-muted-foreground">
            Usa coma para decimales (ej: 1234,56)
          </p>
        )}
      </div>
    );
  }

  // For textarea type, render differently to handle long content
  if (type === "textarea") {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            {Icon && <Icon className="h-4 w-4" />}
            {label}
          </div>
          {disabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Este campo no se puede editar</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startEditing}
                  aria-label={`Editar ${label}`}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                >
                  <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Haz clic para editar</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div
          className={[
            "rounded-md px-2 py-2 -mx-2 focus:outline-none",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/40",
            disabled
              ? "cursor-not-allowed bg-muted/40 text-muted-foreground"
              : "cursor-pointer hover:bg-muted/40",
          ].join(" ")}
          onClick={() => {
            if (!disabled) startEditing();
          }}
          role={!disabled ? "button" : undefined}
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              startEditing();
            }
          }}
        >
          <p className="font-medium whitespace-pre-wrap break-words max-h-[150px] overflow-y-auto">
            {formatValue(value)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </div>
      <div
        className={[
          "flex items-center justify-between gap-2 min-w-0 rounded-md px-2 py-1 -mx-2 focus:outline-none",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/40",
          disabled
            ? "cursor-not-allowed bg-muted/40 text-muted-foreground"
            : "cursor-pointer hover:bg-muted/40 focus-within:bg-muted/40",
        ].join(" ")}
        onClick={() => {
          if (!disabled) startEditing();
        }}
        role={!disabled ? "button" : undefined}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            startEditing();
          }
        }}
      >
        <p className="font-medium truncate min-w-0" title={formatValue(value)}>
          {formatValue(value)}
        </p>
        {disabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Este campo no se puede editar</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  startEditing();
                }}
                aria-label={`Editar ${label}`}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              >
                <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Haz clic para editar</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
